import React, { useState, useEffect, useMemo } from 'react';
import {
  useGetConversations,
  useOpenConversation,
  type ConversationItem,
} from '../../api/conversations';
import { ConversationList } from './ConversationList';
import { ChatActivePane } from './ChatActivePane';
import { pickDisplayName } from './ChatAvatar';
import { showNewMessageToast } from './NewMessageToast';
import { FiX, FiSend, FiMessageSquare, FiBriefcase, FiFolder } from 'react-icons/fi';
import { toast } from '../../utils/toast';
import { useGetServiceApplications } from '../../api/services';
import { useGetGigApplications } from '../../api/queries';
import { useMessageQueueStore } from '../../hooks/useMessageQueue';

interface MessagesViewProps {
  initialConversationUuid?: string;
  highlightOfferUuid?: string;
  onConversationChange?: (uuid: string) => void;
}

export const MessagesView: React.FC<MessagesViewProps> = ({
  initialConversationUuid,
  highlightOfferUuid,
  onConversationChange,
}) => {
  const { data: conversations = [], isLoading } = useGetConversations();
  const openConversationMutation = useOpenConversation();

  // Active chat state
  const [selectedUuid, setSelectedUuid] = useState<string | undefined>(initialConversationUuid);
  const [showMobileChat, setShowMobileChat] = useState(Boolean(initialConversationUuid));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [newlyCreatedConversations, setNewlyCreatedConversations] = useState<
    Record<string, ConversationItem>
  >({});

  // "New Chat" Wizard state
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [contextType, setContextType] = useState<'service_application' | 'gig_application'>(
    'service_application',
  );
  const [selectedContextId, setSelectedContextId] = useState('');
  const [initialMsgText, setInitialMsgText] = useState('');

  // Background notifications for new unread messages in other unmuted chats
  const prevUnreadsRef = React.useRef<Record<string, number>>({});
  const unreadBaselineSetRef = React.useRef(false);

  useEffect(() => {
    if (!conversations || conversations.length === 0) return;

    // The first load only records counts, so existing unread chats don't each pop a toast.
    const notify = unreadBaselineSetRef.current;
    unreadBaselineSetRef.current = true;

    conversations.forEach((conv) => {
      const uuid = conv.conversation_uuid;
      const currentUnread = conv.unread_count || 0;
      const prevUnread = prevUnreadsRef.current[uuid] ?? 0;
      prevUnreadsRef.current[uuid] = currentUnread;

      if (!notify || currentUnread <= prevUnread) return;
      if (uuid === selectedUuid || conv.is_muted) return;

      const other = conv.other_participant;
      showNewMessageToast({
        conversationUuid: uuid,
        senderName:
          pickDisplayName(other?.full_name, other?.username, conv.title, conv.name) || 'Contact',
        avatarUrl: other?.profile_picture || other?.prof_pics || other?.google_prof_pics,
        body: conv.last_message?.body || conv.last_message_text,
        onOpen: () => {
          setSelectedUuid(uuid);
          setShowMobileChat(true);
          onConversationChange?.(uuid);
        },
      });
    });
  }, [conversations, selectedUuid, onConversationChange]);

  // ── Fetch User's Inquiries & Gig Applications for Context selection ────────
  const { data: serviceAppsResp } = useGetServiceApplications({});
  const { data: gigAppsResp } = useGetGigApplications({});

  const serviceApplications = useMemo(() => {
    return serviceAppsResp?.data?.results || [];
  }, [serviceAppsResp]);

  const gigApplications = useMemo(() => {
    return gigAppsResp?.data?.results || [];
  }, [gigAppsResp]);

  // Default to first conversation on desktop if none selected
  useEffect(() => {
    if (
      !selectedUuid &&
      conversations.length > 0 &&
      typeof window !== 'undefined' &&
      window.innerWidth >= 1024
    ) {
      setSelectedUuid(conversations[0].conversation_uuid);
    }
  }, [conversations, selectedUuid]);

  // Keep state synced with prop
  useEffect(() => {
    if (initialConversationUuid) {
      setSelectedUuid(initialConversationUuid);
      setShowMobileChat(true);
    }
  }, [initialConversationUuid]);

  const handleSelectConversation = (conv: ConversationItem) => {
    setSelectedUuid(conv.conversation_uuid);
    setShowMobileChat(true);
    if (onConversationChange) onConversationChange(conv.conversation_uuid);
  };
  const selectedConversation =
    conversations.find((c) => c.conversation_uuid === selectedUuid) ||
    (selectedUuid ? newlyCreatedConversations[selectedUuid] : null) ||
    null;

  // Find recipient ID and context details based on chosen item
  const selectedRecipientInfo = useMemo(() => {
    if (!selectedContextId) return null;

    if (contextType === 'service_application') {
      const found = serviceApplications.find(
        (app) => app.application_uuid === selectedContextId,
      ) as any;
      if (found) {
        const id = found.applicant?.id || found.applicant_id || found.user_id || found.user?.id;
        const name =
          found.applicant?.full_name || found.applicant_email?.split('@')[0] || 'Applicant';
        const email = found.applicant_email || found.applicant?.email;
        const resolvedContextId = found.service?.id?.toString() || '';
        return { id, name, email, resolvedContextId };
      }
    } else {
      const found = gigApplications.find(
        (app) => app.application_uuid === selectedContextId,
      ) as any;
      if (found) {
        const id = found.applicant?.id || found.applicant_id || found.user_id || found.user?.id;
        const name = found.applicant?.full_name || found.applicant?.username || 'Applicant';
        const email = found.applicant?.email || found.applicant_email;
        const resolvedContextId = found.gig?.gig_uuid || '';
        return { id, name, email, resolvedContextId };
      }
    }
    return null;
  }, [selectedContextId, contextType, serviceApplications, gigApplications]);

  const handleStartNewChat = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedContextId) {
      toast.error('Please select an active inquiry or application to message.');
      return;
    }

    const finalRecipientId = selectedRecipientInfo?.id;

    if (!finalRecipientId) {
      toast.error('Could not determine recipient from the selected item.');
      return;
    }

    try {
      const trimmedMsg = initialMsgText.trim();
      const conv = await openConversationMutation.mutateAsync({
        recipient_id: finalRecipientId,
        context_type: contextType === 'service_application' ? 'service' : 'gig',
        context_id: selectedRecipientInfo.resolvedContextId,
      });

      if (conv?.conversation_uuid) {
        setNewlyCreatedConversations((prev) => ({
          ...prev,
          [conv.conversation_uuid]: conv,
        }));
        if (trimmedMsg) {
          useMessageQueueStore.getState().enqueue({
            conversationUuid: conv.conversation_uuid,
            body: trimmedMsg,
          });
        }
        setShowNewChatModal(false);
        setInitialMsgText('');
        setSelectedContextId('');
        handleSelectConversation(conv);
      }
    } catch (error) {
      console.error('Failed to start chat:', error);
      toast.error('Failed to start chat. Please try again.');
    }
  };

  return (
    <div className="flex h-full w-full bg-[#161618] overflow-hidden rounded-[20px] sm:rounded-[28px] border border-[#2C2C2E] shadow-2xl relative">
      {/* ── Sidebar (List) ─────────────────────────────────────────────────── */}
      <div
        className={`w-full shrink-0 border-r border-[#2C2C2E] transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] absolute lg:relative h-full z-10 bg-[#161618] ${
          showMobileChat ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'
        } ${
          sidebarCollapsed
            ? 'lg:w-0 lg:opacity-0 lg:pointer-events-none lg:overflow-hidden'
            : 'lg:w-[360px] xl:w-[400px] lg:opacity-100 lg:pointer-events-auto'
        }`}
      >
        <ConversationList
          conversations={conversations}
          selectedUuid={selectedUuid}
          onSelect={handleSelectConversation}
          onNewChat={() => setShowNewChatModal(true)}
          isLoading={isLoading}
          isCollapsed={sidebarCollapsed}
        />
      </div>

      {/* ── Main Chat Area ─────────────────────────────────────────────────── */}
      <div
        className={`flex-1 transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] absolute lg:relative w-full h-full lg:translate-x-0 bg-[#0F0F11] ${
          showMobileChat ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
        }`}
      >
        {selectedConversation ? (
          <ChatActivePane
            conversation={selectedConversation}
            highlightOfferUuid={highlightOfferUuid}
            onBackMobile={() => setShowMobileChat(false)}
            isSidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        ) : (
          <div className="hidden lg:flex h-full flex-col items-center justify-center text-center p-8 bg-[#161618]">
            <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#1C1C1E] to-[#2A2A2D] border border-[#3A3A3C] shadow-2xl">
              <FiMessageSquare size={36} className="text-[#1784FF]" />
            </div>
            <h3 className="mb-3 text-[24px] font-bold text-white tracking-tight">Your Messages</h3>
            <p className="mb-8 max-w-[280px] text-[15px] text-zinc-400 leading-relaxed">
              Select a conversation from the sidebar or start a new one to begin chatting.
            </p>

            <button
              onClick={() => setShowNewChatModal(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#1784FF] hover:bg-blue-500 px-8 py-3 text-[15px] font-bold text-white transition-all shadow-[0_8px_24px_rgba(23,132,255,0.4)] hover:scale-105 active:scale-95 cursor-pointer"
            >
              <FiMessageSquare size={18} />
              <span>Start a Conversation</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Start New Chat Modal (Context-based) ────────────────────────────── */}
      {showNewChatModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md transition-all duration-300">
          <div className="w-full max-w-2xl bg-[#161618] border border-[#2C2C2E] rounded-[32px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 sm:p-8 animate-in zoom-in-95 fade-in duration-200">
            <div className="flex items-center justify-between pb-6 border-b border-[#2C2C2E]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#1784FF] to-blue-600 text-white shadow-lg shadow-blue-500/20">
                  <FiMessageSquare size={20} />
                </div>
                <h3 className="text-white text-[18px] font-bold tracking-tight">New Message</h3>
              </div>
              <button
                onClick={() => setShowNewChatModal(false)}
                className="flex items-center justify-center w-8 h-8 text-zinc-400 hover:text-white rounded-full bg-[#2A2A2D] hover:bg-[#3A3A3C] transition-colors border border-[#3A3A3C]"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleStartNewChat} className="space-y-5 pt-6">
              {/* Category selector */}
              <div>
                <label className="block text-[13px] font-bold text-zinc-300 mb-2.5 uppercase tracking-wider">
                  Select Category
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setContextType('service_application');
                      setSelectedContextId('');
                    }}
                    className={`flex items-center justify-center gap-2 rounded-2xl py-3.5 text-[14px] font-bold border transition-all ${
                      contextType === 'service_application'
                        ? 'bg-[#1784FF] border-[#1784FF] text-white shadow-[0_4px_16px_rgba(23,132,255,0.3)]'
                        : 'bg-[#1C1C1E] border-[#3A3A3C] text-zinc-400 hover:text-white hover:bg-[#2A2A2D]'
                    }`}
                  >
                    <FiFolder
                      size={16}
                      className={
                        contextType === 'service_application' ? 'text-white' : 'text-zinc-500'
                      }
                    />
                    <span>Inquiries</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setContextType('gig_application');
                      setSelectedContextId('');
                    }}
                    className={`flex items-center justify-center gap-2 rounded-2xl py-3.5 text-[14px] font-bold border transition-all ${
                      contextType === 'gig_application'
                        ? 'bg-[#1784FF] border-[#1784FF] text-white shadow-[0_4px_16px_rgba(23,132,255,0.3)]'
                        : 'bg-[#1C1C1E] border-[#3A3A3C] text-zinc-400 hover:text-white hover:bg-[#2A2A2D]'
                    }`}
                  >
                    <FiBriefcase
                      size={16}
                      className={contextType === 'gig_application' ? 'text-white' : 'text-zinc-500'}
                    />
                    <span>Gig Apps</span>
                  </button>
                </div>
              </div>

              {/* Inquiry/App selector */}
              <div>
                <label className="block text-[13px] font-bold text-zinc-300 mb-2.5 uppercase tracking-wider flex items-center justify-between">
                  <span>
                    Choose Request <span className="text-red-500">*</span>
                  </span>
                </label>
                {contextType === 'service_application' && serviceApplications.length === 0 ? (
                  <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-[13px] text-amber-200/90 leading-relaxed">
                    No active service inquiries found. Conversations must be initiated from guest
                    inquiries.
                  </div>
                ) : contextType === 'gig_application' && gigApplications.length === 0 ? (
                  <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-[13px] text-amber-200/90 leading-relaxed">
                    No active gig applications found. Conversations must be started from active gig
                    applications.
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedContextId}
                      onChange={(e) => setSelectedContextId(e.target.value)}
                      className="w-full bg-[#1C1C1E] border border-[#3A3A3C] text-white text-[14px] rounded-2xl py-3.5 px-4 outline-none focus:border-[#1784FF]/50 focus:ring-1 focus:ring-[#1784FF]/30 transition-all appearance-none"
                    >
                      <option value="">-- Select from list --</option>
                      {contextType === 'service_application'
                        ? serviceApplications.map((app: any) => {
                            const applicantName =
                              app.applicant?.full_name || app.applicant_email || 'Guest';
                            const title = app.service?.title || 'General';
                            return (
                              <option key={app.application_uuid} value={app.application_uuid}>
                                {applicantName} • {title}
                              </option>
                            );
                          })
                        : gigApplications.map((app: any) => {
                            const applicantName =
                              app.applicant?.full_name || app.applicant?.username || 'Applicant';
                            const title = app.gig?.title || 'Gig';
                            return (
                              <option key={app.application_uuid} value={app.application_uuid}>
                                {applicantName} • {title}
                              </option>
                            );
                          })}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-500">
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                        <path
                          d="M1 1.5L6 6.5L11 1.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-bold text-zinc-300 mb-2.5 uppercase tracking-wider">
                  messages{' '}
                  <span className="text-zinc-500 font-normal lowercase tracking-normal">
                    (optional)
                  </span>
                </label>
                <textarea
                  rows={3}
                  value={initialMsgText}
                  onChange={(e) => setInitialMsgText(e.target.value)}
                  placeholder="Say hello..."
                  className="w-full bg-[#1C1C1E] border border-[#3A3A3C] text-white text-[15px] rounded-2xl p-4 outline-none focus:border-[#1784FF]/50 focus:ring-1 focus:ring-[#1784FF]/30 resize-none transition-all placeholder:text-zinc-600"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-6 border-t border-[#2C2C2E] mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewChatModal(false)}
                  className="px-6 py-2.5 rounded-full bg-[#2A2A2D] hover:bg-[#3A3A3C] text-zinc-200 text-[14px] font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={openConversationMutation.isPending || !selectedContextId}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#1784FF] hover:bg-blue-500 text-white text-[14px] font-bold shadow-[0_4px_16px_rgba(23,132,255,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                >
                  {openConversationMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Starting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FiSend size={15} />
                      Start Chat
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
